import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Tooltip } from "@mui/material";

import { Thead, Td, Th, Tr, LargeOnly, SmallOnly } from "commons";
import { IN_PROGRESS, isFailure } from "commons/src/helpers/RequestHelpers";
import { percentageTo2DecimalPlaces } from "commons/src/helpers/FormattingHelpers";
import { isPrintMode } from "commons/src/QueryParameter";

import { Message } from "../../components/Message";
import { ERROR } from "../../helpers/ErrorHelpers";
import { requestObject } from "../../reducers/requestsActions";
import { FolioTable } from "./FolioTable";
import { TableAccordion } from "./TableAccordion";
import { Card } from "./Card";
import { TotalRow } from "./TotalRow";
import { FactSheetLink, LinkText, OpenIcon } from "./FactSheetLink";

export function PortfolioModel({ requestStatus, portfolio = { funds: [] } }) {
    const getAllFunds = (funds = []) => {
        return funds.sort(byFundName).map(p => (
            <Tr key={p.fundId} data-testid="portfolio-model-table-row">
                {p.fundFactSheetAvailable && !isPrintMode() ? (
                    <Td align="left">
                        <FlexCell>
                            <Tooltip title="View fund fact sheet" placement="right" arrow>
                                <FactSheetLink isin={p.isin} fundName={p.fundName}>
                                    <LinkText>{p.fundName}</LinkText>
                                    <LargeOnly>
                                        <OpenIcon />
                                    </LargeOnly>
                                </FactSheetLink>
                            </Tooltip>
                        </FlexCell>
                    </Td>
                ) : (
                    <Td align="left">{p.fundName}</Td>
                )}
                <Td align="right">{percentageTo2DecimalPlaces(p.percentage)}</Td>
            </Tr>
        ));
    };

    const byFundName = (a, b) => {
        if (a.fundName.startsWith("Cash")) {
            return -1;
        } else if (b.fundName.startsWith("Cash")) {
            return 1;
        } else {
            return a.fundName.localeCompare(b.fundName);
        }
    };

    const totalRow = () => (
        <TotalRow key="total-row" data-testid="portfolio-model-total-row">
            <Td align="left">Total</Td>
            <Td align="right">100.00%</Td>
        </TotalRow>
    );

    const allFunds = [...getAllFunds(portfolio.funds), totalRow()];

    return (
        <Fragment>
            <Message
                data-testid="portfolio-error-message"
                severity={ERROR}
                text={requestStatus.errorMessage}
                errorCode={requestStatus.errorCode}
                shown={isFailure(requestStatus.status)}
                padding="0px"
            />
            {!isFailure(requestStatus.status) && portfolio.funds && portfolio.funds.length > 0 && (
                <Card>
                    <FolioTitle>Portfolio name: {portfolio.name}</FolioTitle>
                    <FolioTable data-testid="portfolio-table" dense={isPrintMode()}>
                        <Thead>
                            <Tr data-testid="portfolio-model-header-row">
                                <Th align="left" padding="8px 0px">
                                    Fund name
                                </Th>
                                <Th align="right" padding="8px 10px">
                                    Split
                                </Th>
                            </Tr>
                        </Thead>

                        <LargeOnly enabled={!isPrintMode()}>
                            <tbody>{allFunds}</tbody>
                        </LargeOnly>

                        <SmallOnly>
                            <tbody>
                                {allFunds.slice(0, 3)}
                                <TableAccordion content={allFunds.slice(3)} />
                            </tbody>
                        </SmallOnly>
                    </FolioTable>
                </Card>
            )}
        </Fragment>
    );
}

const FlexCell = styled.div`
    display: flex;
`;

const FolioTitle = styled.div`
    display: none;
    margin: 0.2rem 0 0.5rem 0;
    color: ${props => props.theme.primary};

    @media (max-width: ${props => props.theme.maxWidth}) {
        display: block;
    }

    @media print {
        display: none;
    }
`;

PortfolioModel.defaultProps = {
    requestStatus: {
        status: IN_PROGRESS
    }
};

PortfolioModel.propTypes = {
    accountNum: PropTypes.string,
    clearModal: PropTypes.func,
    openModal: PropTypes.func,
    portfolio: PropTypes.shape({
        name: PropTypes.string,
        shortName: PropTypes.string,
        effectiveDate: PropTypes.string,
        lastUpdatedDate: PropTypes.string,
        funds: PropTypes.arrayOf(
            PropTypes.shape({
                fundName: PropTypes.string,
                percentage: PropTypes.number
            })
        )
    }),
    requestStatus: requestObject
};
